import { useEffect, useState } from "react";
import styled from "styled-components";
import { useDebounceState } from "../../utils/debouncer";
import { useNavigate } from "react-router-dom";
import { AnalyticsAction, logEvent } from "../../utils/analytics";
import { QueryApi } from "../../utils/QueryHelper";
import { AxiosResponse } from "axios";
import { V1Clinic } from "../../types/v1_schemas";
import { useTranslation } from "react-i18next";

const DropDownContainer = styled("div")`
  position: realtive;
  width: 300px;
  @media (max-width: 480px) {
    width: 100%;
    margin: 0 1rem;
  }
`;

const DropDownSearch = styled("input")`
  font-family: "Arial";
  background-repeat: no-repeat;
  background-size: 5%;
  background-position: 3% center;
  width: 100%;
  height: 46px;
  border: 1px solid #1A103F;
  border-radius: 6px;
  padding: 0px 0px 0px 26px;
`;

const SearchClear = styled("svg")`
  position: absolute;
  transform: translateX(-27px) translateY(10px);
  display: inline;
  cursor: pointer;
`;

const DropDownListContainer = styled("div")``;

const DropDownList = styled("div")`
  position: absolute;
  width: 300px;
  background-color: #ffffff;
  opacity: 1;
  z-index: 1;
  border-radius: 4px;
  border: 1px solid #505050;
`;

const ListItem = styled("div")`
  font-family: "Arial";
  padding: 4px 0px 2px 12px;
  border-bottom: 1px solid #a0a0a0;
  cursor: pointer;
  :hover {
    background-color: rgb(238, 238, 238);
  }
`;

const BoldSearch = styled("div")`
  font-weight: bold !important;
  display: inline;
`;

const NoResults = styled("div")`
  margin: 2px 5px;
`;


type AutocompleteProps = {
  closeSearchDlg?: () => void;
  darkBackground?: boolean;
};

export default function Autocomplete({closeSearchDlg = () => {}, darkBackground = false} : AutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [enteredValue, setEnteredValue] = useState("");
  const [filteredData, setFilteredData] = useState([{id: "",name: ""}]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  useEffect(() => {
    if (searchValue.length >= 2){
      let searchFilters = {
        pageSize: "6",
        displayNameFilter: searchValue
      };

      const responseFn = (response: AxiosResponse) => {
        if (response.status === 200) {
          let filtered = response.data.data.map((x: V1Clinic) => ({name: x.displayName, id: x.accountId}));
          setFilteredData(filtered);
          setIsOpen(true);

        } else {
          console.error(`Provider Search: HTTP error! Status: ${response.status}`);
          setFilteredData([]);
          setIsOpen(false);
        }
      };

      const errorFn = (error: any) => {
        console.error(`Provider Search: Axios Error: ${error.message}`);
        setFilteredData([]);
        setIsOpen(false);
      };

      QueryApi(searchFilters, new AbortController().signal, responseFn, errorFn);
    }
    else {
      setFilteredData([]);
      setIsOpen(false);
    }

  }, [searchValue]);

  const onOptionClicked = (value : string) => () => {
    logEvent(AnalyticsAction.SearchProviderByName);

    setIsOpen(false);
    closeSearchDlg();
    setEnteredValue("");
    setSearchValue("");

    navigate(`/result/${value}`);
  };

  const debouncedOnChange = useDebounceState(() => {setSearchValue(enteredValue);}, 500);

  const formatResult = (item: {name: string, id: string}) => {
    let startStr = item.name.toUpperCase().search(searchValue.toUpperCase());

    let firstPart = item.name.slice(0, startStr);
    let midPart = item.name.slice(startStr, startStr + searchValue.length);
    let lastPart = item.name.slice(startStr + searchValue.length);

    return  <ListItem  tabIndex={0} onClick={onOptionClicked(item.id)} key={Math.random()}>
              {firstPart}<BoldSearch>{midPart}</BoldSearch>{lastPart}
            </ListItem>;
  };

  const tonesClass = darkBackground ? "search-dark-class" : "search-light-class";
  const otherClass = darkBackground ? "white nice_font2" : "dark_blue nice_font2";
  const fillPath = darkBackground ? "white" : "rgba(26, 16, 63, 1)"

  return (
    <DropDownContainer onBlur={(event) => {if (!event.currentTarget.contains(event.relatedTarget)) { setIsOpen(false); }}}>
      <DropDownSearch type="text" placeholder={t("providerSearchPlaceholder")} aria-label={t("providerSearchPlaceholder")} autoFocus={true} className={tonesClass + " " + otherClass}
        // debounce sets a delay between keystrokes, this implementation should be safe to use state variables in
        onChange={(e) => {debouncedOnChange(); setEnteredValue(e.target.value);}} 
        value={enteredValue} />
      <SearchClear onClick={() => {setEnteredValue(""); setSearchValue(""); setIsOpen(false);}} width="20" height="20" focusable="false">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.58 12 5 17.58 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill={fillPath}></path>
      </SearchClear>
      {isOpen && (
        <DropDownListContainer className="nice_font2">
          <DropDownList className="nice_font2">
            {filteredData.length > 0 ? 
            filteredData.map(formatResult) :
            <NoResults> No Matching Providers </NoResults>}
          </DropDownList>
        </DropDownListContainer>
      )}
    </DropDownContainer>
  );
}
